import { supabase } from '@/lib/supabase';
import { reportLoginFailure } from '@/lib/reportLoginFailure';
import { otpAuthApi } from '@/api/otpAuth';
import type { AdminUser } from '@/types';

async function loadAdminUser(authUserId: string): Promise<AdminUser> {
  const { data: userRow, error: userError } = await supabase
    .from('users')
    .select('*')
    .eq('auth_user_id', authUserId)
    .single();

  if (userError || !userRow) throw new Error('משתמש לא נמצא');
  if (!userRow.is_super_admin) {
    await supabase.auth.signOut();
    throw new Error('גישה נדחתה — נדרשות הרשאות מנהל ראשי');
  }

  return userRow as AdminUser;
}

export const authApi = {
  /**
   * Password proof only (JumpStart auth_ext). Caller must continue with TOTP
   * via otpAuthApi.verify before a session exists.
   */
  beginLogin: async (email: string, password: string) => {
    try {
      const result = await otpAuthApi.passwordVerify(email, password);
      if (result.next !== 'totp') {
        throw new Error('totp_required');
      }
      return result;
    } catch (err) {
      reportLoginFailure(email, 'admin');
      throw err;
    }
  },

  /** Complete login after authenticator code; returns admin user row. */
  completeLoginWithTotp: async (loginAttempt: string, code: string): Promise<AdminUser> => {
    const data = await otpAuthApi.verify(loginAttempt, code);
    const userId = data.user?.id;
    if (!userId) {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session?.user?.id) throw new Error('Login failed');
      return loadAdminUser(sessionData.session.user.id);
    }
    return loadAdminUser(userId);
  },

  me: async (): Promise<AdminUser | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    const { data } = await supabase
      .from('users')
      .select('*')
      .eq('auth_user_id', session.user.id)
      .single();

    if (!data || !data.is_super_admin) return null;
    return data as AdminUser;
  },

  logout: async () => {
    await supabase.auth.signOut();
  },

  onAuthStateChange: (callback: (user: AdminUser | null) => void) => {
    return supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!session) {
        callback(null);
        return;
      }

      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', session.user.id)
        .single();

      callback(data?.is_super_admin ? (data as AdminUser) : null);
    });
  },
};
